import { useState } from "react";
import { motion } from "framer-motion";
import { MailCheck, Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { matrixStrings as ms } from "../../lib/matrixStrings";

const inputCls =
  "w-full rounded-lg border border-teal-400/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 backdrop-blur transition-colors duration-200 focus:border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-400/20";

export const MatrixContact = ({ lang, initialEmail = "", initialPhone = "", onDone }) => {
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);
  const c = ms.contact;

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const phoneOk = phone.replace(/[^0-9]/g, "").length >= 9;
  const anyOk = emailOk || phoneOk;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-xl"
      data-testid="matrix-contact"
    >
      <p className="text-center font-mono text-[11px] font-semibold tracking-[0.3em] text-teal-400">{c.kicker[lang]}</p>
      <h1 className="font-heading mg-glow-text mt-4 text-center text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="contact-heading">
        {c.heading[lang]}
      </h1>
      <p className="mt-4 text-center text-sm leading-relaxed text-slate-400 sm:text-base">{c.sub[lang]}</p>

      <div className="mg-glass mt-8 rounded-2xl p-6 sm:p-8">
        <label className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-widest text-teal-300/80">
          {c.emailLabel[lang]}
        </label>
        <div className="relative">
          <MailCheck size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={c.emailPh[lang]}
            className={`${inputCls} pl-10`}
            data-testid="contact-email-input"
          />
        </div>

        <label className="mb-1.5 mt-5 block font-mono text-[11px] font-semibold uppercase tracking-widest text-teal-300/80">
          {c.phoneLabel[lang]}
        </label>
        <div className="relative">
          <Phone size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={c.phonePh[lang]}
            className={`${inputCls} pl-10`}
            data-testid="contact-phone-input"
          />
        </div>

        <button
          type="button"
          onClick={() => anyOk && onDone({ email: emailOk ? email.trim() : "", phone: phoneOk ? phone.trim() : "" })}
          disabled={!anyOk}
          data-testid="contact-submit-button"
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-teal-500 py-4 text-sm font-bold uppercase tracking-widest text-[#052E2B] transition-all duration-200 hover:bg-teal-300 hover:shadow-[0_0_30px_rgba(45,212,191,0.4)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {c.btn[lang]} <ArrowRight size={16} />
        </button>
        <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-500">
          <ShieldCheck size={13} className="text-teal-400/70" /> {c.note[lang]}
        </p>
      </div>
    </motion.div>
  );
};
